/**
 * 头像在大厅里的入口：昵称行前面那个可点的小圆头像，点开 `AvatarPickerSheet`。
 * 与 `EmailAuthEntry.tsx` 同一套「hook 管开关 + 入口 + 面板」结构。
 */
import { useCallback, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicAccount } from '@poker/shared'

import { ownAvatarVersion, resolveAvatar } from '../table/avatar.js'
import { AvatarPickerSheet } from './AvatarPickerSheet.js'
import './avatarPicker.css'

export interface AvatarEntryState {
  readonly open: boolean
  show(): void
  close(): void
}

export function useAvatarEntry(): AvatarEntryState {
  const [open, setOpen] = useState(false)
  return {
    open,
    show: useCallback(() => setOpen(true), []),
    close: useCallback(() => setOpen(false), []),
  }
}

/** 昵称行前面那个可点的小圆头像。身份还没准备好时画一个空的占位圈，避免布局跳动。 */
export function AvatarButton({
  account,
  onOpen,
}: {
  readonly account: PublicAccount | null
  onOpen(): void
}): ReactNode {
  const avatar = resolveAvatar(account?.avatarSeed ?? '', account?.id ?? '', ownAvatarVersion())
  return (
    <button type="button" className="avatar-entry-btn" aria-label="头像与语音" onClick={onOpen}>
      {avatar.kind === 'custom' ? (
        <img className="avatar-entry-btn__img" src={avatar.url} alt="" width={32} height={32} />
      ) : avatar.kind === 'emoji' ? (
        avatar.char
      ) : (
        <span className="avatar-entry-btn__placeholder" aria-hidden="true" />
      )}
    </button>
  )
}

export interface AvatarEntrySheetProps {
  readonly state: AvatarEntryState
  readonly account: PublicAccount | null
}

export function AvatarEntrySheet({ state, account }: AvatarEntrySheetProps): ReactNode {
  if (!state.open) return null
  return <AvatarPickerSheet account={account} onClose={state.close} />
}
